import { LayerSpecification } from "maplibre-gl";
import {
  MaplibreLayer,
  Layer as MbtLayer,
  MaplibreStyleLayer as MbtMaplibreStyleLayer,
} from "mobility-toolbox-js/ol";
import Layer from "ol/layer/Layer";

import { MAPSET_METADATA_FILTER } from "./constants";

/**
 * Returns true if the layer contains the correct metadata.
 *
 * @param {Object} layer a mapbox style layer
 * @param {Regex} regex  The regex to test the metadata value with
 */
export const hasMetadataFilter = (
  layer,
  regex,
  filterAttribute = MAPSET_METADATA_FILTER,
) => {
  if (Array.isArray(filterAttribute) && Array.isArray(regex)) {
    return (
      filterAttribute.filter((attr, index) => {
        // When using an array, negative lookahead regex must work
        return regex[index].test(layer?.metadata?.[attr] ?? undefined);
      })?.length === filterAttribute.length
    );
  }
  return (
    !!layer.metadata &&
    !!layer.metadata[filterAttribute] &&
    regex.test(layer.metadata[filterAttribute])
  );
};

/**
 * Get a specific layer from mapbox map, using the mapset.filter metadata value.
 * @param layer mapbox layer containing the mbMap
 * @param metadataValue Value fo the mapset.filter metadata property
 */
export const getStyleLayersByMetadataFilter = (
  layer,
  metadataValue,
  filterAttribute = MAPSET_METADATA_FILTER,
) => {
  if (!layer || !layer.mbMap) {
    return;
  }
  const styleObject = layer.mbMap.getStyle();
  const { layers } = styleObject;

  return layers.filter((styleLayer) => {
    return hasMetadataFilter(styleLayer, metadataValue, filterAttribute);
  });
};

/**
 * Create filters for buslines
 */
export const createBusFilter = (filterItemsArray, lineProvidersArray) => {
  const customFilters = [];

  filterItemsArray.map((f) => {
    if (f.lines !== "" && f.provider !== "") {
      customFilters.push(["in", f.lines, ["get", `lines ${f.provider}`]]);
    } else if (f.provider === "") {
      // Case for specified line only
      lineProvidersArray.forEach((i) => {
        return customFilters.push(["in", f.lines, ["get", `lines ${i}`]]);
      });
    } else if (f.lines === "") {
      // Case for specified provider only
      customFilters.push(["has", `lines ${f.provider}`]);
    }
  });
  return customFilters;
};

class MaplibreStyleLayer extends MbtMaplibreStyleLayer {
  applyLayoutVisibility(evt) {
    try {
      // TODO: find why the style is not ready sometimes and triggers an ugly error
      super.applyLayoutVisibility(evt);
    } catch {
      // console.error('Style is not ready', err);
    }
  }
}
/**
 *
 * @param {Object} options options for mobility-toolbox-js MaplibreStyleLayer
 * @returns mobility-toolbox-js MaplibreStyleLayer or Layer
 */
export const createMaplibreStyleLayer = (options) => {
  const properties = {
    ...options,
    layersFilter: (layer: LayerSpecification) => {
      return hasMetadataFilter(
        layer,
        options.properties?.filterRegex,
        options.properties?.filterAttribute,
      );
    },
  };
  if (options.children) {
    properties.children = options.children.map(createMaplibreStyleLayer);
    return new MbtLayer(properties);
  }
  return new MaplibreStyleLayer(properties);
};

/**
 * @param {MaplibreStyleLayer} layer The mobility-toolbox-js layer to set the maplibre layer
 * @param {MaplibreLayer} maplibreLayer The maplibre layer to set
 */
export const setMaplibreLayer = (
  layer: Layer,
  maplibreLayer: MaplibreLayer,
) => {
  const children = layer.get("children") as Layer[];
  if (children?.length > 0) {
    children.forEach((child) => {
      return setMaplibreLayer(child, maplibreLayer);
    });
    return;
  }
  (layer as MaplibreStyleLayer).maplibreLayer = maplibreLayer;
};
